import React, { useState, useEffect } from 'react';
import { ArrowRightLeft, Filter, Calendar, Package, MapPin, Truck, BarChart3 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { LocationTransfer, Product, Location } from '../types';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import Input from '../components/ui/Input';
import Badge from '../components/ui/Badge';

const LocationTransfersPage: React.FC = () => {
  const { user } = useAuth();
  const [transfers, setTransfers] = useState<LocationTransfer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showTransferForm, setShowTransferForm] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [transferForm, setTransferForm] = useState({
    from_location_id: '',
    to_location_id: '',
    quantity: 1,
    reason: '',
    notes: ''
  });
  const [filters, setFilters] = useState({
    status: '',
    product_id: '',
    location_id: '',
    date_from: '',
    date_to: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load transfers
      const { data: transfersData, error: transfersError } = await supabase
        .from('location_transfers')
        .select(`
          *,
          from_location:locations!location_transfers_from_location_id_fkey(*),
          to_location:locations!location_transfers_to_location_id_fkey(*),
          product:products(*)
        `)
        .order('created_at', { ascending: false });

      if (transfersError) throw transfersError;
      setTransfers(transfersData || []);

      // Load products for filter
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('id, name, sku')
        .order('name');

      if (productsError) throw productsError;
      setProducts(productsData || []);

      // Load locations for filter
      const { data: locationsData, error: locationsError } = await supabase
        .from('locations')
        .select('id, name')
        .order('name');

      if (locationsError) throw locationsError;
      setLocations(locationsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear any previous errors
    setError(null);
    
    try {
      setLoading(true);
      
      // Validate form data
      if (!selectedProduct) {
        setError('Please select a product');
        setLoading(false);
        return;
      }
      
      if (!transferForm.from_location_id || transferForm.from_location_id === '') {
        setError('Please select a source location');
        setLoading(false);
        return;
      }
      
      if (!transferForm.to_location_id || transferForm.to_location_id === '') {
        setError('Please select a destination location');
        setLoading(false);
        return;
      }
      
      if (transferForm.from_location_id === transferForm.to_location_id) {
        setError('Source and destination locations cannot be the same');
        setLoading(false);
        return;
      }
      
      if (transferForm.quantity <= 0) {
        setError('Quantity must be greater than zero');
        setLoading(false);
        return;
      }
      
      const { data: transferId, error } = await supabase.rpc('transfer_inventory', {
        p_user_id: user?.id,
        p_product_id: selectedProduct,
        p_from_location_id: transferForm.from_location_id,
        p_to_location_id: transferForm.to_location_id,
        p_quantity: transferForm.quantity,
        p_reason: transferForm.reason,
        p_notes: transferForm.notes
      });

      if (error) throw error;
      
      setShowTransferForm(false);
      setTransferForm({
        from_location_id: '',
        to_location_id: '',
        quantity: 0,
        reason: '',
        notes: ''
      });
      setSelectedProduct('');
      loadData();
    } catch (error: any) {
      console.error('Transfer error:', error);
      alert(`Transfer failed: ${error.message || 'Unknown error'}`);
      setError(error.message || 'Failed to transfer inventory');
    } finally {
      setLoading(false);
    }
  };

  const filteredTransfers = transfers.filter(transfer => {
    if (filters.status && transfer.status !== filters.status) return false;
    if (filters.product_id && transfer.product_id !== filters.product_id) return false;
    if (filters.location_id && 
        transfer.from_location_id !== filters.location_id && 
        transfer.to_location_id !== filters.location_id) return false;
    if (filters.date_from && new Date(transfer.created_at) < new Date(filters.date_from)) return false;
    if (filters.date_to && new Date(transfer.created_at) > new Date(filters.date_to)) return false;
    return true;
  });

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'danger';
      default: return 'secondary';
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const productOptions = [
    { value: '', label: 'All Products' },
    ...products.map(product => ({
      value: product.id,
      label: `${product.name} (${product.sku})`
    }))
  ];

  const locationOptions = [
    { value: '', label: 'All Locations' },
    ...locations.map(location => ({
      value: location.id,
      label: location.name
    }))
  ];

  const transferReasons = [
    { value: '', label: 'Select Reason' },
    { value: 'restock', label: 'Restocking' },
    { value: 'demand', label: 'High Demand' },
    { value: 'maintenance', label: 'Location Maintenance' },
    { value: 'optimization', label: 'Inventory Optimization' },
    { value: 'other', label: 'Other' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Banner */}
      <div className="overflow-hidden rounded-lg">
        <div className="relative">
          <img
            src="https://images.pexels.com/photos/906494/pexels-photo-906494.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
            alt="Forklift moving inventory between warehouse storage locations"
            className="w-full h-64 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30 flex items-center">
            <div className="px-8 text-white">
              <h1 className="text-3xl font-bold mb-2">Location Transfers</h1>
              <p className="text-lg opacity-90">
                Move inventory between storage locations efficiently
              </p>
              <div className="mt-4 flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <ArrowRightLeft className="h-4 w-4 mr-2" />
                  <span>{transfers.length} Total Transfers</span>
                </div>
                <div className="flex items-center">
                  <Truck className="h-4 w-4 mr-2" />
                  <span>Multi-location Operations</span>
                </div>
                <div className="flex items-center">
                  <BarChart3 className="h-4 w-4 mr-2" />
                  <span>Transfer Analytics</span>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute top-4 right-4 flex gap-2">
            <Button
              variant="secondary"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </Button>
            <Button
              onClick={() => setShowTransferForm(true)}
            >
              <ArrowRightLeft className="h-4 w-4 mr-2" />
              New Transfer
            </Button>
          </div>
        </div>
      </div>

      {showFilters && (
        <Card>
          <CardContent className="py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <Select
                label="Status"
                options={statusOptions}
                value={filters.status}
                onChange={(value) => setFilters({ ...filters, status: value })}
                fullWidth
              />
              
              <Select
                label="Product"
                options={productOptions}
                value={filters.product_id}
                onChange={(value) => setFilters({ ...filters, product_id: value })}
                fullWidth
              />
              
              <Select
                label="Location"
                options={locationOptions}
                value={filters.location_id}
                onChange={(value) => setFilters({ ...filters, location_id: value })}
                fullWidth
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From Date
                </label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-blue-900"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  To Date
                </label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-blue-900"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Transfer History</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredTransfers.length === 0 ? (
            <div className="text-center py-8">
              <ArrowRightLeft className="h-12 w-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No transfers found</h3>
              <p className="text-gray-500">No location transfers match your current filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      From Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      To Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reason
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTransfers.map((transfer) => (
                    <tr key={transfer.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 text-gray-600 mr-2" />
                          <div className="text-sm text-gray-900">
                            {formatDate(transfer.created_at)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Package className="h-4 w-4 text-gray-600 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {transfer.product?.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              SKU: {transfer.product?.sku}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 text-gray-600 mr-2" />
                          <div className="text-sm text-gray-900">
                            {transfer.from_location?.name || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 text-gray-600 mr-2" />
                          <div className="text-sm text-gray-900">
                            {transfer.to_location?.name || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {transfer.quantity} units
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={getStatusBadgeVariant(transfer.status)}>
                          {transfer.status}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {transfer.reason || 'N/A'}
                        </div>
                        {transfer.notes && (
                          <div className="text-sm text-gray-500 mt-1">
                            {transfer.notes}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Transfer Form Modal */}
      {showTransferForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Transfer Inventory</CardTitle>
            </CardHeader>
            <form onSubmit={handleTransfer}>
              <CardContent className="space-y-4">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">{error}</span>
                  </div>
                )}
                
                <Select
                  label="Product"
                  options={[
                    { value: '', label: 'Select Product' },
                    ...products.map(product => ({
                      value: product.id,
                      label: `${product.name} (${product.sku})`
                    }))
                  ]}
                  value={selectedProduct}
                  onChange={(value) => setSelectedProduct(value)}
                  required
                  error={!selectedProduct ? "Please select a product" : ""}
                  fullWidth
                />
                
                <Select
                  label="From Location"
                  options={[
                    { value: '', label: 'Select Source Location' },
                    ...locations.map(location => ({
                      value: location.id,
                      label: location.name
                    }))
                  ]}
                  value={transferForm.from_location_id}
                  onChange={(value) => {
                    setTransferForm({ ...transferForm, from_location_id: value });
                    if (error && !value) setError('Please select a source location');
                    else if (error) setError(null);
                  }}
                  required
                  error={!transferForm.from_location_id ? "Please select a source location" : ""}
                  fullWidth
                />
                
                <Select
                  label="To Location"
                  options={[
                    { value: '', label: 'Select Destination Location' },
                    ...locations.map(location => ({
                      value: location.id,
                      label: location.name
                    }))
                  ]}
                  value={transferForm.to_location_id}
                  onChange={(value) => {
                    setTransferForm({ ...transferForm, to_location_id: value });
                    if (error && !value) setError('Please select a destination location');
                    else if (error) setError(null);
                  }}
                  required
                  error={!transferForm.to_location_id ? "Please select a destination location" : ""}
                  fullWidth
                />

                <Input
                  label="Quantity"
                  type="number"
                  min="1"
                  value={transferForm.quantity}
                  onChange={(e) => setTransferForm({ ...transferForm, quantity: parseInt(e.target.value) || 1 })}
                  required
                  fullWidth
                />

                <Select
                  label="Reason"
                  options={transferReasons}
                  value={transferForm.reason}
                  onChange={(value) => setTransferForm({ ...transferForm, reason: value })}
                  fullWidth
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={transferForm.notes}
                    onChange={(e) => setTransferForm({ ...transferForm, notes: e.target.value })}
                    rows={3}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-blue-900"
                  />
                </div>
              </CardContent>
              <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowTransferForm(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  Transfer
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

export default LocationTransfersPage;